import React, { useEffect, useState } from 'react';

import {
  MinusOutlined,
  PlusOutlined,
  RadiusSettingOutlined,
} from '@ant-design/icons';
import { Button, Tooltip, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { Circle, Layer, Line, Stage } from 'react-konva';
import './index.scss';

const RADIUS_POINT = 5;

const Edge = ({ node1, node2 }) => {
  return (
    <Line
      points={[node1.x, node1.y, node2.x, node2.y]}
      stroke="#eb5648"
      strokeWidth={1}
    />
  );
};

const limitValue = (value, minValue, maxValue) => {
  let v = value;
  v = Math.max(minValue, v);
  v = Math.min(v, maxValue);
  return v;
};

const parsePoints = (v) => {
  const result = [];
  const pointStr = v || '';
  const polygons = pointStr.split('|');

  polygons.forEach((polygonStr) => {
    const polyPoints = {};

    polygonStr.split(';').forEach((pointStr, pointIdx) => {
      const x = parseFloat(pointStr.split(',')[0]);
      const y = parseFloat(pointStr.split(',')[1]);
      polyPoints[String(pointIdx)] = { x, y };
    });

    result.push(polyPoints);
  });

  return result;
};

const DEFAULT_REGION = {
  0: {
    x: 0.2,
    y: 0.2,
  },
  1: {
    x: 0.6,
    y: 0.2,
  },
  2: {
    x: 0.8,
    y: 0.7,
  },
};

export const DrawPolygon = ({
  width,
  height,
  bgImage,
  strPoints,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const [dataPoints, setDataPoints] = useState({});
  const [dataLinks, setDataLinks] = useState({});
  const [edges, setEdges] = useState([]);
  const [anchors, setAnchors] = useState([]);

  const [activePolygonIndex, setActivePolygonIndex] = useState('0');

  const buildLinks = (polygonIdx, numPoints) => {
    const newLinks = [];
    for (let i = 0; i < numPoints; i++) {
      if (i === numPoints - 1) {
        newLinks.push([`${polygonIdx}_${i}`, `${polygonIdx}_0`]);
      } else {
        newLinks.push([`${polygonIdx}_${i}`, `${polygonIdx}_${i + 1}`]);
      }
    }

    return newLinks;
  };

  const addPoint = () => {
    if (!activePolygonIndex) {
      notification.warning({
        message: 'Please select a region to adding points',
        placement: 'bottomRight',
      });
      return;
    }

    if (dataPoints && dataPoints[activePolygonIndex]) {
      const length = dataPoints[activePolygonIndex].length;
      const activePoints = dataPoints[activePolygonIndex];

      const lastPoint =
        length > 0
          ? dataPoints[activePolygonIndex][length - 1]
          : {
              x: width / 2,
              y: height / 2,
            };

      setDataPoints({
        ...dataPoints,
        [activePolygonIndex]: [
          ...activePoints,
          {
            id: `${activePolygonIndex}_${length}`,
            x: limitValue(lastPoint.x + 15, 0, width),
            y: limitValue(lastPoint.y + 15, 0, height),
            width: RADIUS_POINT,
            height: RADIUS_POINT,
            draggable: true,
            radius: RADIUS_POINT,
          },
        ],
      });

      const newLinks = buildLinks(activePolygonIndex, length + 1);
      setDataLinks({ ...dataLinks, [activePolygonIndex]: newLinks });
    }
  };

  const removePoint = () => {
    if (dataPoints && dataPoints[activePolygonIndex]) {
      const activePoints = dataPoints[activePolygonIndex];
      const activePointsLength = dataPoints[activePolygonIndex].length;

      if (activePointsLength < 3 && activePointsLength > 0) {
        notification.warning({
          message: 'The region is deleted',
          placement: 'bottomRight',
        });
        delete dataPoints[activePolygonIndex];
        delete dataLinks[activePolygonIndex];

        setDataPoints({ ...dataPoints });
        setDataLinks({ ...dataLinks });
        setActivePolygonIndex('');
        return;
      }

      const newPoints = activePoints.slice(0, activePoints.length - 1);
      setDataPoints({ ...dataPoints, [activePolygonIndex]: newPoints });
      const newLinks = buildLinks(activePolygonIndex, newPoints.length);
      setDataLinks({ ...dataLinks, [activePolygonIndex]: newLinks });
    }
  };

  const addNewRegion = () => {
    const newPolygonIdx = Object.keys(dataPoints).length;

    const newRegionLinks = buildLinks(newPolygonIdx, 3);
    const newPoints = [];
    Object.keys(DEFAULT_REGION).forEach((pointIdx) => {
      newPoints.push({
        id: `${newPolygonIdx}_${pointIdx}`,
        x: limitValue(DEFAULT_REGION[pointIdx].x * width, 0, width),
        y: limitValue(DEFAULT_REGION[pointIdx].y * height, 0, height),
        width: RADIUS_POINT,
        height: RADIUS_POINT,
        draggable: true,
        radius: RADIUS_POINT,
      });
    });

    console.log('Add region: setDataLinks: ', {
      ...dataLinks,
      [newPolygonIdx]: newRegionLinks,
    });
    console.log('add region: setDataPoints: ', {
      ...dataPoints,
      [newPolygonIdx]: newPoints,
    });
    setDataLinks({ ...dataLinks, [newPolygonIdx]: newRegionLinks });
    setDataPoints({ ...dataPoints, [newPolygonIdx]: newPoints });
  };

  useEffect(() => {
    const mapPoints = {};
    const mapLinks = {};
    const points = parsePoints(strPoints);

    (points || []).forEach((polygonPoints, idx) => {
      mapPoints[String(idx)] = Object.keys(polygonPoints || []).map(
        (pointIdx) => {
          return {
            id: `${idx}_${pointIdx}`,
            x: limitValue(points[idx][pointIdx].x * width, 0, width),
            y: limitValue(points[idx][pointIdx].y * height, 0, height),
            width: RADIUS_POINT,
            height: RADIUS_POINT,
            draggable: false,
            radius: RADIUS_POINT,
          };
        }
      );
    });

    Object.keys(mapPoints).forEach((item) => {
      mapLinks[item] = buildLinks(item, mapPoints[item].length);
    });

    setDataPoints(mapPoints);
    setDataLinks(mapLinks);
  }, [height, width, strPoints]);

  useEffect(() => {
    const newEdges = [];

    Object.keys(dataLinks || {}).forEach((polygonIdx) => {
      const links = dataLinks[polygonIdx];
      links.forEach((link, linkIdx) => {
        const node1 = dataPoints[polygonIdx].filter(
          (obj) => obj.id === link[0]
        )[0];

        const node2 = dataPoints[polygonIdx].filter(
          (obj) => obj.id === link[1]
        )[0];

        newEdges.push([node1, node2]);
      });
    });

    setEdges(newEdges);
  }, [dataLinks, dataPoints]);

  useEffect(() => {
    const newAnchors = [];

    Object.keys(dataPoints).forEach((polygonIdx) => {
      const polygonPoints = dataPoints[polygonIdx];
      polygonPoints.forEach((point, idx) => {
        newAnchors.push(point);
      });
    });

    setAnchors(newAnchors);
  }, [dataLinks, dataPoints]);

  useEffect(() => {
    if (typeof onChange === 'function') {
      const result = [];
      Object.keys(dataPoints).forEach((polygonId) => {
        let polygonPoints = [];
        dataPoints[polygonId].forEach((p) => {
          polygonPoints.push(
            `${(p.x / width).toFixed(2)},${(p.y / height).toFixed(2)}`
          );
        });
        result.push(polygonPoints.join(';'));
      });
      onChange(result.join('|'));
    }
  }, [dataPoints, onChange, width, height]);

  return (
    <div className="draw-polygon">
      <div className="controls" style={{ width: width }}>
        <div>
          <Tooltip title={t('Remove a point')} placement="bottom">
            <Button
              disabled={disabled}
              style={{ marginRight: '2px' }}
              size="small"
              icon={<MinusOutlined />}
              onClick={removePoint}
            />
          </Tooltip>
          <Tooltip title={t('Add a point')} placement="bottom">
            <Button
              disabled={disabled}
              size="small"
              icon={<PlusOutlined />}
              onClick={addPoint}
            />
          </Tooltip>
        </div>

        <div>
          <Tooltip title={t('Add a region')} placement="bottom">
            <Button
              size="small"
              style={{ marginRight: '2px' }}
              icon={<RadiusSettingOutlined />}
              onClick={addNewRegion}
              disabled={disabled}
            />
          </Tooltip>
        </div>
      </div>
      <div className="wrap-draw-polygon">
        <img
          className="bg-img"
          src={bgImage}
          width={width}
          height={height}
          alt="Add region"
        />
        <div className="draw-region">
          <Stage width={width} height={height}>
            <Layer>
              {edges.map((edge, idx) => {
                return (
                  <Edge key={`edge-${idx}`} node1={edge[0]} node2={edge[1]} />
                );
              })}

              {anchors.map((point, idx) => {
                const polygonIdx = point.id.split('_')[0];

                return (
                  <Circle
                    key={`point-${idx}`}
                    {...point}
                    fill={
                      activePolygonIndex === polygonIdx ? '#eb5648' : '#ffc93c'
                    }
                    onClick={(e) => {
                      const pointId = e.target.attrs.id;
                      const polygonIdx = pointId.split('_')[0];
                      setActivePolygonIndex(polygonIdx);
                    }}
                    radius={RADIUS_POINT}
                    draggable={!disabled}
                    onDragMove={(e) => {
                      let xPoint = 0;
                      let yPoint = 0;

                      const pointId = e.target.attrs.id;
                      const polygonIdx = pointId.split('_')[0];
                      setActivePolygonIndex(polygonIdx);

                      const newDataPoints = { ...dataPoints };

                      let pointsTemp = dataPoints[polygonIdx].map((p1) => {
                        if (p1.id === e.target.attrs.id) {
                          const attrs = e.target.attrs;
                          xPoint = limitValue(attrs.x, 0, width);
                          yPoint = limitValue(attrs.y, 0, height);

                          return {
                            ...attrs,
                            x: xPoint,
                            y: yPoint,
                            radius: RADIUS_POINT,
                          };
                        }
                        return p1;
                      });

                      e.currentTarget.setRadius(RADIUS_POINT);

                      if (xPoint >= 0) {
                        e.currentTarget.setX(xPoint);
                      }

                      if (yPoint >= 0) {
                        e.currentTarget.setY(yPoint);
                      }

                      newDataPoints[polygonIdx] = pointsTemp;
                      setDataPoints(newDataPoints);
                    }}
                  />
                );
              })}
            </Layer>
          </Stage>
        </div>
      </div>
    </div>
  );
};

export default DrawPolygon;
